// Header.jsx
import "../Css/header2.css";
import React, { useEffect, useState, useMemo, useRef } from "react";
import Prismic from "@prismicio/client";
import { Fade } from "react-awesome-reveal";

import Logo from "../Assets/logo_harshit.png";
import FigmaMeasureTag from "./FigmaMeasureTag";
import AsciiPortrait from "./AsciiPortrait";

// Normalizes a worklist ("main") or morework ("more") item into the shape
// the "Recently Added" card renders, since the two custom types use
// different field names for the same concepts (images vs logo,
// long_description vs small_description, etc).
function normalizeRecentWork(source, item) {
  const isMain = source === "main";
  const uid = item?.data?.content_relationship?.uid;
  const href =
    isMain && item?.data?.onclicklink
      ? item?.data?.link
      : uid
        ? `/case-study/${uid}`
        : null;
  if (!href) return null;

  return {
    thumb: isMain ? item?.data?.images?.[0]?.image_link : item?.data?.logo,
    title: item?.data?.title,
    description: isMain
      ? item?.data?.long_description
      : item?.data?.small_description,
    href,
    external: isMain && Boolean(item?.data?.onclicklink),
  };
}

// Picks whichever case study — from either pool — was actually published
// or updated most recently, rather than always defaulting to morework's
// manually-curated "order" field. NDA entries are skipped (nothing public
// to highlight), and first_publication_date is the fallback for a document
// that's never been republished since it was first created.
function pickMostRecentWork(mainWorkDocs, moreWorkDocs) {
  const candidates = [
    ...(mainWorkDocs || []).map((item) => ({ source: "main", item })),
    ...(moreWorkDocs || []).map((item) => ({ source: "more", item })),
  ].filter(({ item }) => !item?.data?.nda);

  let best = null;
  let bestDate = null;
  for (const { source, item } of candidates) {
    const dateStr = item?.last_publication_date || item?.first_publication_date;
    const date = dateStr ? new Date(dateStr).getTime() : NaN;
    if (Number.isNaN(date)) continue;
    if (!best || date > bestDate) {
      const normalized = normalizeRecentWork(source, item);
      if (normalized) {
        best = normalized;
        bestDate = date;
      }
    }
  }
  return best;
}

function Header({ doc: docProp, mainWorkDoc: mainWorkDocProp } = {}) {
  const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
  const accessToken = process.env.REACT_APP_PRISMIC_TOKEN;
  const Client = Prismic.client(apiEndpoint, { accessToken });
  // A parent (e.g. the home page) is managing this fetch if `doc` was passed
  // at all — even while it's still `null`/loading. Only self-fetch when no
  // parent is providing it.
  const hasDocProp = docProp !== undefined;
  const hasMainWorkDocProp = mainWorkDocProp !== undefined;
  const [fetchedDoc, setFetchedDoc] = useState(null);
  const [fetchedMainWorkDoc, setFetchedMainWorkDoc] = useState(null);
  const [ownIsLoading, setOwnIsLoading] = useState(true);
  const doc = hasDocProp ? docProp : fetchedDoc;
  const mainWorkDoc = hasMainWorkDocProp ? mainWorkDocProp : fetchedMainWorkDoc;
  const isRecentWorkLoading =
    (hasDocProp ? docProp === null : ownIsLoading) ||
    (hasMainWorkDocProp ? mainWorkDocProp === null : ownIsLoading);

  useEffect(() => {
    // If a parent already fetched these (e.g. the home page), don't fetch again.
    if (hasDocProp && hasMainWorkDocProp) return;

    const fetchData = async () => {
      const [moreRes, mainRes] = await Promise.all([
        hasDocProp
          ? Promise.resolve(null)
          : Client.query(Prismic.Predicates.at("document.type", "morework"), {
              orderings: "[my.morework.order desc]",
            }),
        hasMainWorkDocProp
          ? Promise.resolve(null)
          : Client.query(Prismic.Predicates.at("document.type", "worklist"), {
              orderings: "[my.worklist.order desc]",
            }),
      ]);
      if (moreRes) setFetchedDoc(moreRes.results);
      if (mainRes) setFetchedMainWorkDoc(mainRes.results);
      setOwnIsLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDocProp, hasMainWorkDocProp]);

  const recentWork = useMemo(
    () => pickMostRecentWork(mainWorkDoc, doc),
    [mainWorkDoc, doc],
  );

  // Must match .grid-lines' own grid-template-columns/rows (16 x 8) in
  // header2.css — a mismatch here (this used to be 18 x 9) means the extra
  // cells overflow into implicit grid rows that CSS auto-sizes to near
  // nothing, rendering as stray thin lines at the bottom of the grid.
  const totalCells = 16 * 8;

  // Each cell draws in from a random edge, computed once so it doesn't reshuffle on re-render.
  const cellDirections = useMemo(() => {
    const directions = ["draw-left", "draw-right", "draw-top", "draw-bottom"];
    return Array.from(
      { length: totalCells },
      () => directions[Math.floor(Math.random() * directions.length)],
    );
  }, [totalCells]);

  // Plays the grid draw-in animation once, the first time it scrolls into view.
  const gridLinesRef = useRef(null);
  const [gridInView, setGridInView] = useState(false);

  useEffect(() => {
    if (!gridLinesRef.current || gridInView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGridInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(gridLinesRef.current);
    return () => observer.disconnect();
  }, [gridInView]);

  return (
    <div className="hero">
      <nav className="hero-nav">
        <div className="nav-grid-lines">
          {Array.from({ length: 16 }).map((_, i) => (
            <div
              className="nav-line-cell"
              key={i}
              style={{ "--line-delay": `${i * 0.03}s` }}
            />
          ))}
        </div>

        <div className="hero-brand">
          <a href="/">
            {" "}
            <img src={Logo} alt="logo" className="hero-logo-icon" />
          </a>
          <div className="hero-nav-links">
            <a href="/archives">Archives</a>,<a href="/about">About</a>,
            <a href="#contact">Contact</a>
          </div>
        </div>

        <div className="hero-nav-info">
          <div className="info-block">
            <span>Currently Lead</span>
            <span>Product Designer @ MakseGroup</span>
          </div>
          <div className="info-block">
            <span>Based in</span>
            <span>Gurugram,India</span>
          </div>
        </div>
      </nav>
      <div className="hero-grid">
        <div
          className={`grid-lines ${gridInView ? "in-view" : ""}`}
          ref={gridLinesRef}
        >
          {Array.from({ length: totalCells }).map((_, i) => (
            <div
              className={`grid-line-cell ${cellDirections[i]}`}
              key={i}
              style={{ animationDelay: `${(i % 16) * 0.1}s` }}
            />
          ))}
        </div>

        <div className="grid-content">
          <div className="content-cell">
            <Fade delay={100} triggerOnce={false}>
              <div className="badge">
                <span className="dot"></span>
                Available for Work
              </div>
            </Fade>

            <Fade delay={150} cascade damping={0.3} triggerOnce={false}>
              <h1 className="hero-heading">
                Lead Product{" "}
                <FigmaMeasureTag position="top" showDimensions={false}>
                  Designer
                </FigmaMeasureTag>{" "}
                with{" "}
                <span className="accent"> 6+ Years of Experience</span>
              </h1>
            </Fade>

            <Fade delay={200} triggerOnce={false}>
              <p className="hero-para">
                Yo, <span className="highlight">Harshit Sharma</span>,
                building AI-native experiences. Design +
                Engineering + AI workflows, helping founders ship products that
                actually think.
              </p>
            </Fade>
          </div>

          <div className="logo-card-cell">
            <div className="logo-card">
              <AsciiPortrait src={Logo} alt="Harshit Sharma logo" />
            </div>
          </div>

          <div className="case-study-cell">
            {isRecentWorkLoading ? (
              <div className="case-study-card">
                <div className="cs-thumb">
                  <div
                    className="skeleton"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
                <div className="cs-info">
                  <span className="cs-tag">
                    <span className="tag-dot"></span>
                    RECENTLY ADDED
                  </span>
                  <div
                    className="skeleton skeleton-text"
                    style={{ width: "90%", marginBottom: 6 }}
                  />
                  <div
                    className="skeleton skeleton-text"
                    style={{ width: "60%" }}
                  />
                </div>
              </div>
            ) : recentWork ? (
              <div className="case-study-card">
                <div className="cs-thumb">
                  <img src={recentWork.thumb} alt={recentWork.title || ""} />
                </div>
                <div className="cs-info">
                  <span className="cs-tag">
                    <span className="tag-dot"></span>
                    RECENTLY ADDED
                  </span>
                  <p>{recentWork.description}</p>

                  <a
                    className="cs-cta"
                    href={recentWork.href}
                    {...(recentWork.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    Read Full Case Study →
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
